import type { BoxProps } from '@mui/material/Box';

import { getUrl } from '@/utils';
import { Button, Card } from '@mui/material';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { m } from 'framer-motion';
import { varFade, AnimateText, MotionContainer, animateTextClasses } from '@/components/animate';
import { CONFIG } from '@/global-config';
import { HeroSection } from '@/types/sections/HeroSection';
import Icon, { getIconName } from '@/components/Icon/Icon';
import { getCtaUrl } from '@/types/Cta';

// ----------------------------------------------------------------------

type HeroProps = BoxProps & {
  section: HeroSection;
};

export default function Hero({ sx, section, ...other }: HeroProps) {
  const title = [section?.word, section?.title].filter(
    (line): line is string => Boolean(line)
  );

  const images = [...(section?.image?.url ? [`url(${section.image.url})`] : [])];

  const ctaLink1 = getUrl(section?.cta1);
  const ctaLink2 = getUrl(section?.cta2);

  return (
    <Box
      component="section"
      sx={[
        (theme) => ({
          ...theme.mixins.bgGradient({
            images,
          }),
          height: { md: 560 },
          py: { xs: 10, md: 0 },
          overflow: 'hidden',
          position: 'relative',
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}>
      <Container component={MotionContainer}>
        <Box
          sx={{
            bottom: { md: 80 },
            position: { md: 'absolute' },
            textAlign: { xs: 'center', md: 'unset' },
          }}>
          <Card sx={{ borderRadius: 0, p: 4, maxWidth: { xs: '100%', md: 760 } }}>
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <Typography
                component="h4"
                variant="h2"
                sx={{
                  m: 0,
                  p: 0,
                  overflowWrap: 'break-word',
                  wordBreak: 'normal',
                }}>
                {section?.word && (
                  <Box component="span" sx={{ display: 'block', color: 'primary.main' }}>
                    {section.word}
                  </Box>
                )}
                {section?.title && (
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      color: section?.word ? 'text.primary' : 'primary.main',
                    }}>
                    {section.title}
                  </Box>
                )}
              </Typography>
            </Box>

            <AnimateText
              component="h4"
              variant="h2"
              textContent={title}
              variants={varFade('inRight', { distance: 24 })}
              sx={{
                display: { xs: 'none', md: 'inherit' },
                color: 'primary.main',
              }}
            />

            <m.div variants={varFade('inUp', { distance: 24 })}>
              <Typography sx={{ mt: 3, color: 'text.secondary', fontWeight: 'fontWeightSemiBold' }}>
                {section?.subtitle}
              </Typography>
            </m.div>

            <m.div variants={varFade('inUp', { distance: 24 })}>
              {ctaLink2 && section?.cta2 && (
                <Button
                  size="large"
                  color="primary"
                  variant="contained"
                  href={getCtaUrl(section.cta2)}
                  sx={{
                    mt: 4,
                    mr: 2,
                    borderRadius: 0,
                    bgcolor: 'common.white',
                    color: 'primary.main',
                    border: 1,
                    borderColor: 'primary.lighter',
                  }}
                  startIcon={
                    section.cta2.icon && (
                      <Icon
                        name={getIconName(section.cta2.icon, 'unknown')}
                        color={'primary.lighter'}
                      />
                    )
                  }>
                  {section.cta2.title}
                </Button>
              )}
              {ctaLink1 && section?.cta1 && (
                <Button
                  size="large"
                  color="primary"
                  variant="contained"
                  href={getCtaUrl(section.cta1)}
                  sx={{ mt: 4, mr: 2, borderRadius: 0 }}
                  startIcon={
                    section.cta1.icon && (
                      <Icon
                        name={getIconName(section.cta1.icon, 'unknown')}
                        color={'common.white'}
                      />
                    )
                  }>
                  {section.cta1.title}
                </Button>
              )}
            </m.div>
          </Card>
        </Box>
      </Container>
    </Box>
  );
}
